import React from 'react'
import { isShippingForDifferentMonth } from 'src/Libs/shop/utils'
import { BasicSystemResponseModel, ProductData } from 'src/Libs/shop/models/basic-system.model'
import { isSameMonth, setMonth, setYear } from '@e3dc-react/shell/Libs/date'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ShopSystemConfigurationTypes } from 'src/Libs/shop/types'
import useModals from 'src/Hooks/useModals'
import { useShopCart } from 'src/Libs/shop/hooks'
import { useShopChannelType } from 'src/Libs/shop/hooks/use-shop-channel-type'
import { ShopChannelType } from 'src/Libs/shop/utils/shop-channel-rules'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import BasicSystemTableMonthCell from 'src/Libs/shop/components/SystemConfiguration/BasicSystem/BasicSystemTableMonthCell'

export interface BasicSystemConfiguratorItem {
  desiredShippingDate?: {
    month?: number | null
    year?: number | null
  } | null
  configuration: {
    configuratorId: string | null
  }
}

export interface BasicSystemTableMonthCellWrapperProps {
  isCellHeaderEmpty: boolean
  month: ProductData
  configuratorItems: BasicSystemConfiguratorItem[]
  existingSystemConfiguratorShippingDate: Date | null
  cartSystemConfigurationIds: string[] | undefined
  existingSystemConfigurationType?: ShopSystemConfigurationTypes
  productData: BasicSystemResponseModel
  handleBasicSystemConfigurationChange: (
    selectedConfigurator: string | null,
    productName: string,
    productId: string,
    productYear: string,
    productMonth: string
  ) => void
}

/*
 * Handles Normal Basic System month cell logic and passes state to the base month cell
 */
const BasicSystemTableMonthCellWrapper: React.FC<BasicSystemTableMonthCellWrapperProps> = ({
  isCellHeaderEmpty,
  month,
  configuratorItems,
  existingSystemConfiguratorShippingDate,
  existingSystemConfigurationType,
  cartSystemConfigurationIds,
  productData,
  handleBasicSystemConfigurationChange,
}) => {
  const { t } = useCustomTranslation()
  const { openShopLeavingConfirmation } = useModals()
  const { deleteProductCart } = useShopCart()
  const { enqueueWarningStack } = useSnackbar()
  const shopChannel = useShopChannelType()

  const selectedTime = configuratorItems.length > 0 ? configuratorItems[0].desiredShippingDate : null
  const selectedConfigurator = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const isMatchingMonth = Number(month?.month) === selectedTime?.month && month?.year === selectedTime?.year?.toString()
  const productNumber = month?.productNumber
  const isMatchingConfigurator = productNumber !== undefined && productData.elements[productNumber]?.id === selectedConfigurator

  const isFixNotAvailable = Number(month?.monthData?.fixAvailable) === 0
  const isFlexAvailable = Number(month?.monthData?.flexAvailable) > 0
  const isFixAvailable = Number(month?.monthData?.fixAvailable) > 0

  const shopSelectedChannel = shopChannel ?? ShopChannelType.ShopE3dc
  const isMonthSelectable = isShippingForDifferentMonth(month?.month, month?.year, shopSelectedChannel)

  const currentMonthDate =
    month?.year !== undefined && month?.month !== undefined ? setMonth(setYear(new Date(), Number(month.year)), Number(month.month)) : null

  const isDesired =
    currentMonthDate && existingSystemConfiguratorShippingDate ? isSameMonth(existingSystemConfiguratorShippingDate, currentMonthDate) : false

  const isFixSelected = isDesired && existingSystemConfigurationType === ShopSystemConfigurationTypes.Fix
  const isFlexSelected = isDesired && existingSystemConfigurationType === ShopSystemConfigurationTypes.Flex
  const isFlexDisabled = isMonthSelectable || !isFlexAvailable
  const isFixDisabled = isMonthSelectable || !isFixAvailable
  const highlightFlex = isFlexSelected && isFlexAvailable && !isFixAvailable
  const highlightFix = isFixSelected && isFixAvailable

  const handleSystemConfigurationChange = async (
    selectedConfiguratorValue: string | null,
    productName: string,
    productId: string,
    selectedYear: string,
    selectedMonth: string
  ): Promise<void> => {
    if (existingSystemConfiguratorShippingDate && cartSystemConfigurationIds) {
      if (!highlightFlex && !highlightFix) {
        return openShopLeavingConfirmation({
          title: t('shop.system-configurator.override-system-title'),
          text: t('shop.system-configurator.override-system-text'),
          confirmButtonText: t('shop.system-configurator.override-yes'),
          cancelButtonText: t('shop.system-configurator.override-no'),
          onConfirm: async onClose => {
            await deleteProductCart(cartSystemConfigurationIds)
            handleBasicSystemConfigurationChange(selectedConfiguratorValue, productName, productId, selectedYear, selectedMonth)
            onClose()
          },
        })
      }
    }

    handleBasicSystemConfigurationChange(selectedConfiguratorValue, productName, productId, selectedYear, selectedMonth)
  }

  const onFlexClick = async (): Promise<void> => {
    if (month.monthData.fixAvailable > 0) {
      enqueueWarningStack(t('shop.basic-system.fix-available-error-message'))
    }

    await handleSystemConfigurationChange(
      selectedConfigurator,
      productData.elements[month.productNumber].translated.name,
      productData.elements[month.productNumber].id,
      month.year,
      month.month
    )
  }

  const onFixClick = async (): Promise<void> => {
    await handleSystemConfigurationChange(
      selectedConfigurator,
      productData.elements[month.productNumber].translated.name,
      productData.elements[month.productNumber].id,
      month.year,
      month.month
    )
  }

  return (
    <BasicSystemTableMonthCell
      isCellHeaderEmpty={isCellHeaderEmpty}
      month={month}
      fixAvailableCount={month?.monthData?.fixAvailable ?? 0}
      fixButtonDisabled={isFixDisabled}
      flexButtonDisabled={isFlexDisabled}
      fixButtonActive={Boolean(isMatchingMonth && isMatchingConfigurator && isFixAvailable)}
      flexButtonActive={Boolean(isMatchingMonth && isMatchingConfigurator && isFixNotAvailable && isFlexAvailable)}
      fixButtonHighlight={highlightFix}
      flexButtonHighlight={highlightFlex}
      onFixClick={onFixClick}
      onFlexClick={onFlexClick}
    />
  )
}

export default BasicSystemTableMonthCellWrapper
